import * as React from 'react';
import * as moment from 'moment';
import { toast } from 'react-toastify';
import { IBaseResponse, IListItem, IPhlebotRequest } from '@nkr/models/index';
import { Alert, Button, Col, Modal, ControlLabel, FormControl, FormGroup, Jumbotron, Panel, Label, Row, PanelGroup } from 'react-bootstrap';
import { PhlebotStatus } from '@nkr/enums/index';

interface IProps {
    isAdminView: boolean,
    data: IPhlebotRequest,
    types: IListItem[],
    states: IListItem[],
    onUpdate: (request: IPhlebotRequest) => void,
    onUpdateRequestToSentStatus: (requestId: number) => Promise<IBaseResponse>,
    onReorderRequest: (reqId: number) => void,
    onUpdateRequestInViewToSent: (reqId: number) => void
}

interface IState {
    [index: string]: string | boolean | number | Date | IPhlebotRequest,
    request: IPhlebotRequest,
    showModal: boolean
}

class ViewPhlebotRequestContent extends React.Component<IProps, IState> {
    readonly state: IState = {
        request: null,
        showModal: false
    };

    componentDidMount = () => {
        this.setState({
            request: this.props.data
        });
    }

    handleClose = () => {
        this.setState({ showModal: false });
    }

    handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            request: { ...this.state.request, [name]: value }
        });
    }

    handleStateChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let value = e.currentTarget.value;

        this.setState({
            request: { ...this.state.request, state: parseInt(value) }
        });
    }

    handleReorderRequest = () => {
        this.props.onReorderRequest(this.props.data.id);
    }

    handleMarkAsSent = () => {
        if (this.state.request.trackingId != this.props.data.trackingId) {
            this.props.onUpdate(this.state.request);
        }

        this.props.onUpdateRequestToSentStatus(this.props.data.id)
            .then((response) => {
                if (response.data.success) {
                    this.setState({
                        request: { ...this.state.request, status: PhlebotStatus.Sent }
                    });
                    toast.success(`Status for ${this.props.data.firstName} ${this.props.data.lastName} has been updated`);
                } else {
                    toast.error("Unable to update status");
                }
            });

        this.handleClose();
        this.props.onUpdateRequestInViewToSent(this.state.request.id);
    }

    handleRequestUpdate = () => {
        this.props.onUpdate(this.state.request);
    }

    toggleModal = () => {
        this.setState({
            showModal: !this.state.showModal
        });
    }

    render = () => {
        if (this.props.data == null || this.state.request == null)
            return null;

        let examOneLink = (this.props.data.transactionUniqueId && moment(this.props.data.dateCreated).isSameOrAfter('2023-06-19'))
            ? "https://portal.examone.com/Order/GetOrderDetails/" + this.props.data.transactionUniqueId
            : "";

        let showActionItems = this.props.isAdminView && this.state.request.status == PhlebotStatus.Pending;
        
        return (
            <>
                <Alert bsStyle='info' style={{ marginBottom: 10 }}>
                    <Row>
                        <Col xs={12} md={10} lg={5} style={{ fontWeight: 500 }}>Type:</Col>
                        <Col xs={12} md={2} lg={7}><Label bsStyle='danger'>{this.props.data.description}</Label></Col>
                    </Row>

                    <Row>
                        <Col xs={12} md={10} lg={5} style={{ fontWeight: 500 }}>Status:</Col>
                        <Col xs={12} md={2} lg={7}>
                            <Label bsStyle='info' style={{ color: '#333' }}>{PhlebotStatus[this.state.request.status].replace(/([A-Z])/g, ' $1').trim()}</Label>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} md={10} lg={5} style={{ fontWeight: 500 }}>Alias:</Col>
                        <Col xs={12} md={2} lg={7}>{this.props.data.alias != undefined ? this.props.data.alias : "N/A"}</Col>
                    </Row>

                    <Row>
                        <Col xs={12} md={10} lg={5} style={{ fontWeight: 500 }}>Date Created:</Col>
                        <Col xs={12} md={2} lg={7}>
                            {moment(this.props.data.dateCreated).isValid()
                                ? moment(this.props.data.dateCreated).format("MM/DD/YYYY")
                                : "N/A"}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} md={10} lg={5} style={{ fontWeight: 500 }}>Date Updated:</Col>
                        <Col xs={12} md={2} lg={7}>
                            {moment(this.props.data.dateUpdated).isValid()
                                ? moment(this.props.data.dateUpdated).format("MM/DD/YYYY")
                                : "N/A"}</Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={10} lg={5} style={{ fontWeight: 500 }}>Date Scheduled:</Col>
                        <Col xs={12} md={2} lg={7}>
                            {moment(this.props.data.dateScheduled).isValid()
                                ? moment(this.props.data.dateScheduled).format("MM/DD/YYYY")
                                : "N/A"}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={5} style={{ fontWeight: 500 }}>Date Completed:</Col>
                        <Col xs={12} md={2} lg={7}>
                            {moment(this.props.data.dateCompleted).isValid()
                                ? moment(this.props.data.dateCompleted).format("MM/DD/YYYY")
                                : "N/A"}
                        </Col>
                    </Row>
                </Alert>


                {showActionItems &&
                    <Row>
                        <Col md={6}>
                            <Button bsStyle="primary" onClick={this.handleReorderRequest} block>
                                Reorder Request
                            </Button>
                        </Col>
                        <Col md={6}>
                            <Button bsStyle="primary" onClick={this.toggleModal} block>
                                Mark as Sent
                            </Button>
                        </Col>
                    </Row>
                }

                <PanelGroup>
                    <Panel>
                        <Panel.Heading>Request Information</Panel.Heading>
                        <Panel.Body>
                            <Row>
                                <Col sm={6} md={6}>
                                    <ControlLabel>Vendor Response Details: </ControlLabel>
                                </Col>
                                <Col sm={6} md={6}>
                                    {this.state.request.officeContactDetails ?? "--"}
                                </Col>
                            </Row>

                            <Row>
                                <Col sm={6} md={6}>
                                    <ControlLabel>Tracking Id (Internal Use): </ControlLabel>
                                </Col>
                                <Col sm={6} md={6}>
                                    {this.state.request.trackingId ?? "--"}
                                </Col>
                            </Row>

                            <Row>
                                <Col sm={6} md={6}>
                                    <ControlLabel>Vendor Order Id: </ControlLabel>
                                </Col>
                                <Col sm={6} md={6}>
                                    {this.state.request.transactionUniqueId ?? "--"}
                                </Col>
                            </Row>

                            <Row>
                                <Col sm={6} md={6}>
                                    <ControlLabel>Notes: </ControlLabel>
                                </Col>
                                <Col sm={6} md={6}>
                                    {this.state.request.notes ?? "--"}
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>

                    <Panel>
                        <Panel.Heading>Personal Information</Panel.Heading>
                        <Panel.Body>
                            {!this.props.isAdminView ?
                                <>
                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>First Name</ControlLabel>{' '}
                                        </Col>
                                        <Col sm={6} md={6}>
                                            {this.state.request.firstName ?? '--'}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>Last Name</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {this.state.request.lastName ?? '--'}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>Date of Birth</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {moment(this.state.request.birthDate).format("L")}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>Phone Number</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {this.state.request.phoneNumber ?? '--'}
                                        </Col>
                                    </Row>
                                </>

                                //Admin View below
                                :
                                <>
                                    <Row>
                                        <Col sm={12} md={6}>
                                            <ControlLabel>First Name</ControlLabel>{' '}
                                            {!this.props.isAdminView
                                                ? this.state.request.firstName
                                                : <FormControl
                                                    type="text"
                                                    name="firstName"
                                                    value={this.state.request.firstName}
                                                    onChange={this.handleInputChange} />
                                            }
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup>
                                                <ControlLabel>Last Name</ControlLabel>{' '}
                                                {!this.props.isAdminView
                                                    ? this.state.request.lastName
                                                    : <FormControl
                                                        type="text"
                                                        name="lastName"
                                                        value={this.state.request.lastName}
                                                        onChange={this.handleInputChange} />
                                                }
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={12} md={6}>
                                            <ControlLabel>Date of Birth</ControlLabel>{' '}

                                            {!this.props.isAdminView
                                                ? moment(this.state.request.birthDate).format("MM/dd/yyyy")
                                                : <FormControl
                                                    type="date"
                                                    name="birthDate"
                                                    value={moment(this.state.request.birthDate).format("YYYY-MM-DD")}
                                                    onChange={this.handleInputChange} />
                                            }
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup>
                                                <ControlLabel>Phone Number</ControlLabel>{' '}
                                                {!this.props.isAdminView
                                                    ? this.state.request.phoneNumber
                                                    : <FormControl
                                                        type="text"
                                                        name="phoneNumber"
                                                        value={this.state.request.phoneNumber}
                                                        onChange={this.handleInputChange} />
                                                }

                                            </FormGroup>{' '}
                                        </Col>
                                    </Row>
                                </>
                            }
                        </Panel.Body>
                    </Panel>

                    <Panel>
                        <Panel.Heading>Address Information</Panel.Heading>
                        <Panel.Body>
                            {!this.props.isAdminView ?
                                <>
                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>Address 1</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {this.state.request.address}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>Address 2</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {this.state.request.address2 ?? '--'}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>City</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {this.state.request.city}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={6} md={6}>
                                            <ControlLabel>State</ControlLabel>{' '}
                                        </Col>

                                        <Col sm={6} md={6}>
                                            {this.props.states.find((item) => item.id == this.state.request.state).description}
                                        </Col>
                                    </Row>
                                </>

                                //Admin View below
                                :
                                <>
                                    <Row>
                                        <Col sm={12} md={12}>
                                            <FormGroup>
                                                <ControlLabel>Address 1</ControlLabel>{' '}
                                                {!this.props.isAdminView
                                                    ? this.state.request.address
                                                    : <FormControl
                                                        type="text"
                                                        name="address"
                                                        value={this.state.request.address}
                                                        onChange={this.handleInputChange} />
                                                }

                                            </FormGroup>{' '}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={12} md={12}>
                                            <FormGroup>
                                                <ControlLabel>Address 2</ControlLabel>{' '}
                                                {!this.props.isAdminView
                                                    ? this.state.request.address2
                                                    : <FormControl
                                                        type="text"
                                                        name="address2"
                                                        value={this.state.request.address2 ?? undefined}
                                                        onChange={this.handleInputChange} />
                                                }

                                            </FormGroup>{' '}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm={12} md={6}>
                                            <FormGroup>
                                                <ControlLabel>City</ControlLabel>{' '}
                                                {!this.props.isAdminView
                                                    ? this.state.request.city
                                                    : <FormControl
                                                        type="text"
                                                        name="city"
                                                        value={this.state.request.city}
                                                        onChange={this.handleInputChange} />
                                                }
                                            </FormGroup>{' '}
                                        </Col>

                                        <Col sm={12} md={3}>
                                            <ControlLabel>State</ControlLabel>{' '}
                                            {!this.props.isAdminView
                                                ? this.state.request.state
                                                : <FormControl
                                                    componentClass='select'
                                                    value={this.state.request.state}
                                                    onChange={this.handleStateChange}
                                                >
                                                    <option value={''}>- Select State -</option>
                                                    {this.props.states.map(item =>
                                                        <option
                                                            key={"state_" + item.id} value={item.id}>{item.description}</option>
                                                    )}
                                                </FormControl>
                                            }
                                        </Col>

                                        <Col sm={12} md={3}>
                                            <ControlLabel>Postal Code</ControlLabel>{' '}
                                            {!this.props.isAdminView
                                                ? this.state.request.postalCode
                                                : <FormControl
                                                    name="postalCode"
                                                    type="text"
                                                    value={this.state.request.postalCode}
                                                    onChange={this.handleInputChange}
                                                />
                                            }
                                        </Col>
                                    </Row>
                                </>
                            }
                        </Panel.Body>
                    </Panel>
                </PanelGroup>

                <Row>
                    <Col sm={12} md={12}>
                        {!this.props.isAdminView
                            ? this.state.request.state
                            : <Button bsStyle="primary" onClick={this.handleRequestUpdate} block>
                                Update Request
                            </Button>
                        }
                    </Col>
                </Row>

                <Modal style={{ zIndex: 10000 }} show={this.state.showModal} onHide={this.handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>Validation Required</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5>Please validate the following data before proceeding:</h5>
                        <hr />
                        <Row>
                            <Col sm={12} md={12}>
                                <ControlLabel>Tracking Id</ControlLabel>{' '}

                                <FormControl
                                    type="text"
                                    name="trackingId"
                                    value={this.state.request.trackingId}
                                    onChange={this.handleInputChange}
                                />{' '}
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button bsStyle="primary" onClick={this.handleMarkAsSent} disabled={this.state.request.trackingId.length == 0}>
                            Confirm Tracking ID
                        </Button>
                        <Button onClick={this.handleClose}>Cancel</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default ViewPhlebotRequestContent;